import { Component, ElementRef, forwardRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormGroup } from '@angular/forms';
import { GetMembersQuery, MemberModel } from '@bayclubs/group-classes-employee-api';
import { Subscription } from 'rxjs';
import { debounceTime, first } from 'rxjs/operators';
import { MemberApiService } from '../../services/member-api.service';
import { MemberSearchFormService } from '../../services/member-search-form.service';

const MEMBER_SEARCH_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MemberSearchComponent),
    multi: true
};

@Component({
    selector: 'app-member-search',
    templateUrl: './member-search.component.html',
    styleUrls: ['./member-search.component.scss'],
    providers: [MEMBER_SEARCH_CONTROL_VALUE_ACCESSOR, MemberSearchFormService],
    standalone: false
})
export class MemberSearchComponent implements OnInit, OnDestroy, ControlValueAccessor {
    @Input() disabled: boolean = false;
    @Input() dropDownWidth: number = null;
    @Input() isValid: boolean = true;
    @ViewChild('controlRoot', { static: true }) controlRoot: ElementRef;

    public members: MemberModel[] = [];
    selectedMember: MemberModel;
    memberForm: UntypedFormGroup;
    public searchVisible = false;
    public isBusy = false;
    subscriptions: Subscription;

    onChange = (member: MemberModel) => {};
    onTouched = () => {};

    constructor(private memberFormService: MemberSearchFormService, private memberApiService: MemberApiService) {}

    ngOnInit() {
        this.subscriptions = new Subscription();
        this.memberForm = this.memberFormService.getMemberForm();
        this.subscriptions.add(
            this.memberFormService
                .getSearchPhraseValueChanges()
                .pipe(debounceTime(500))
                .subscribe(() => {
                    this.searchMembers();
                })
        );
        this.searchMembers();
    }

    ngOnDestroy() {
        this.memberFormService.onDestroy();
        this.subscriptions.unsubscribe();
    }

    public get selectedMemberName(): string {
        if (this.selectedMember) {
            return this.selectedMember.firstName + ' ' + this.selectedMember.lastName;
        }
        return '';
    }

    public onFocus() {
        this.onEnter();
    }

    public onEnter() {
        this.searchVisible = true;
    }

    public onLostFocus() {
        this.searchVisible = false;
    }
    onSearch() {
        if (this.isBusy) {
            return;
        }
        this.searchMembers();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event) {
        if (!this.disabled) {
            if (this.controlRoot.nativeElement.contains(event.target) && !this.selectedMember) {
                this.onEnter();
            } else {
                this.onLostFocus();
            }
        }
    }

    public onItemClick(item: MemberModel) {
        this.searchVisible = false;
        this.onChange(item);
        this.selectedMember = item;
    }

    public get isDropDownWidthProvided(): boolean {
        return this.dropDownWidth !== null;
    }

    public get hasItems() {
        return this.members && this.members.length > 0;
    }

    searchMembers() {
        if (this.memberForm.valid) {
            this.isBusy = true;

            const request = this.prepareRequest();
            this.memberApiService
                .get(request)
                .pipe(first())
                .subscribe({
                    next: result => {
                        this.members = result.items;
                    },
                    complete: () => {
                        this.isBusy = false;
                    }
                });
        }
    }

    private prepareRequest(): GetMembersQuery {
        return <GetMembersQuery>{ searchPhrase: this.memberFormService.getSearchPhrase() };
    }

    writeValue(member: MemberModel): void {
        this.memberFormService.setFormValue(member);
        this.selectedMember = member;
    }
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
